import { Component, Input } from '@angular/core';
import { AutoIBC } from '../../../../../models/api/view/claim/details/auto/auto-ibc';
import { BaseComponent } from '../../../../base/base.component';
import { CardComponent } from '../../../../core/panels/card.component';
import { DetailItemComponent } from '../../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../../core/panels/detail-panel/detail-panel.component';

@Component({
  selector: 'app-ibc-au',
  imports: [DetailPanelComponent, DetailItemComponent, CardComponent],
  template: `
    <div class="tw-flex tw-w-full tw-gap-8 tw-pt-4 tw-pb-4">
      <app-detail-panel [dataSource]="locationData" class="tw-basis-3/5">
        <app-detail-item
          label="Type&nbsp;of&nbsp;use"
          name="typeOfUseDisplay"
        />
        <app-detail-item
          label="Statistical&nbsp;territory"
          name="statisticalTerritoryDisplay"
        />
      </app-detail-panel>
      <app-detail-panel [dataSource]="locationData" class="tw-basis-2/5">
        <app-detail-item label="Location province" name="locationProvince" />
        <app-detail-item label="Claim coverage" name="coverageDisplay" />
        <app-detail-item label="Kind of loss" name="kindOfLossDisplay" />
        <app-detail-item
          label="Vehicle location postal code"
          name="locationPostalCode"
        />
      </app-detail-panel>
    </div>
    <div class="tw-flex tw-w-full tw-gap-8 tw-pt-4">
      <app-card label="First party" class="tw-basis-1/3">
        <app-detail-panel [dataSource]="firstPartyData">
          <app-detail-item label="Driver last name" name="lastName" />
          <app-detail-item label="Driver first name" name="firstName" />
          <app-detail-item label="Driver middle name" name="middleName" />
          <app-detail-item label="Driver province" name="driverState" />
          <app-detail-item
            label="Driver's license number"
            name="licenseNumber"
          />
          <app-detail-item
            label="Driver's license province"
            name="licenseState"
          />
          <app-detail-item label="Vehicle indicator" name="vehicleType" />
          <app-detail-item
            label="Percent fault"
            name="percentFault"
            format="p"
          />
        </app-detail-panel>
      </app-card>
      <app-card label="Third party" class="tw-basis-1/3">
        <app-detail-panel [dataSource]="thirdPartyData">
          <app-detail-item label="Driver last name" name="lastName" />
          <app-detail-item label="Driver first name" name="firstName" />
          <app-detail-item label="Driver middle name" name="middleName" />
          <app-detail-item label="Driver province" name="driverState" />
          <app-detail-item
            label="Driver's license number"
            name="licenseNumber"
          />
          <app-detail-item
            label="Driver's license province"
            name="licenseState"
          />
          <app-detail-item
            label="Company ID"
            name="companyDisplay"
            labelCase="none"
          />
          <app-detail-item label="Policy ID" name="policyID" labelCase="none" />
          <app-detail-item
            label="Vehicle utilization type"
            name="vehicleUtilTypeDisplay"
          />
          <app-detail-item
            label="Percent fault"
            name="percentFault"
            format="p"
          />
        </app-detail-panel>
      </app-card>
      <app-card label="Details" class="tw-basis-1/3">
        <app-detail-panel [dataSource]="financialData">
          <app-detail-item
            label="Claimant annual income"
            name="claimantAnnualIncome"
            format="c"
          />
          <app-detail-item
            label="Legal reserve"
            name="legalReserve"
            format="c"
          />
          <app-detail-item
            label="AB catastrophic"
            name="abCatastrophicDisplay"
            labelCase="none"
          />
          <app-detail-item
            label="AB minor injury"
            name="abMinorInjuryDisplay"
            labelCase="none"
          />
        </app-detail-panel>
      </app-card>
    </div>
  `,
  styles: ``,
})
export class IbcAuComponent extends BaseComponent {
  @Input() ibcData: AutoIBC;

  get locationData() {
    const loc = this.ibcData?.location || {};
    return {
      ...loc,
      typeOfUseDisplay: this.formatCodeWithDescription(
        loc.typeOfUse,
        loc.typeOfUseDescription,
      ),
      statisticalTerritoryDisplay: this.formatCodeWithDescription(
        loc.statisticalTerritory,
        loc.statisticalTerritoryDescription,
      ),
      coverageDisplay: this.formatCodeWithDescription(
        loc.coverageCode,
        loc.coverageCodeDescription,
      ),
      kindOfLossDisplay: this.formatCodeWithDescription(
        loc.kindOfLoss,
        loc.kindOfLossDescription,
      ),
    };
  }

  get firstPartyData() {
    return this.ibcData?.firstPartyDriver || {};
  }

  get thirdPartyData() {
    const tp = this.ibcData?.thirdPartyDriver || {};
    return {
      ...tp,
      companyDisplay: this.formatCodeWithDescription(
        tp.companyID,
        tp.companyIDDescription,
      ),
      vehicleUtilTypeDisplay: this.formatCodeWithDescription(
        tp.vehicleUtilType,
        tp.vehicleUtilTypeDescription,
      ),
    };
  }

  get financialData() {
    const fin = this.ibcData?.financialInfo || {};
    return {
      ...fin,
      abCatastrophicDisplay: this.formatCodeWithDescription(
        fin.abCatastrophic,
        fin.abCatastrophicDescription,
      ),
      abMinorInjuryDisplay: this.formatCodeWithDescription(
        fin.abMinorInjury,
        fin.abMinorInjuryDescription,
      ),
    };
  }

  /**
   * Format code with description: "CODE - Description" or just description if no code
   */
  private formatCodeWithDescription(code: string, description: string): string {
    if (code && description) {
      return `${code} - ${description}`;
    }
    return description || code || '';
  }
}
